// Match-rate widget — BTTS + Clean Sheets (Phase 3, half-donut type).
//
// One shared component; a `kind` variant fixes the data source, id-prefix, view
// class names, and per-match predicate (like TopScorers/Assists). Two views,
// toggled by a legacy button that flips `mod.dataset.{btts,cs}View` and calls
// refreshModuleBody — the value arrives here as `view`:
//   - "donut"  — a two-segment half-donut per team, via <HalfDonut> (keyed by
//                fixture, so a fixture change snaps like the legacy rebuild; an
//                ha/timeline change animates). Adds the {btts,cs}-donut-view
//                class to the wrapper.
//   - "blocks" — a static per-team grid of recent results (no D3, plain JSX),
//                matching the legacy blocks innerHTML.
// Emits the same class names + DOM as the legacy renderers (verified by the A/B).
//
// Data now comes from app-api via `useWidgetData` (was the getBttsData /
// getCleanSheetData / getFormData("recent") legacyData getters). Each title
// fetches ONE variant-keyed endpoint (`btts` | `clean-sheets`) returning a
// `MatchRatePayload` envelope `{ rates, form }` — the donut view reads `rates`,
// the blocks view reads `form` (recent results). Both keys return the SAME
// payload type, so — unlike FormBadges/TopScorers — no per-branch cast is
// needed. This is the wave that finally frees `getFormData` (MatchRate was its
// last live consumer).

import { shapeRateDonut, shapeRateBlocks, type RateKind } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import HalfDonut, { type DonutConfig } from "./HalfDonut.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

/** Per-kind constants: which data + the class-name family the CSS keys off. */
const CONFIG: Record<
  RateKind,
  { idPrefix: string; donutView: string; blockView: string; blockRow: string }
> = {
  btts: {
    idPrefix: "bt",
    donutView: "btts-donut-view",
    blockView: "btts-block-view",
    blockRow: "btts-block-row",
  },
  cleanSheet: {
    idPrefix: "cs",
    donutView: "cs-donut-view",
    blockView: "cs-block-view",
    blockRow: "cs-block-row",
  },
};

export interface MatchRateProps {
  kind: RateKind;
  fixture: string;
  ha?: string | null;
  timeline?: string | null;
  /** "donut" (default) | "blocks". */
  view?: string | null;
}

export default function MatchRate({
  kind,
  fixture,
  ha,
  timeline,
  view,
}: MatchRateProps) {
  const cfg = CONFIG[kind];
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const t1 = (parts[0] ?? "").trim();
  const t2 = (parts[1] ?? "").trim();
  const teams = t2 ? [t1, t2] : [t1];

  // One fetch per title; both keys return `MatchRatePayload` so no cast needed.
  const { data, isPending, isError, refetch } = useWidgetData(
    kind === "btts" ? "btts" : "clean-sheets",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (view === "blocks") {
    const recent = data?.form ?? {};
    return (
      <div className={`data-wrapper fg-wrapper ${cfg.blockView}`}>
        {teams.map((team) => (
          <div className={cfg.blockRow} key={team}>
            <div className="fg-label">{team}</div>
            <div className="form-container">
              {shapeRateBlocks(recent[team], team, ha, timeline, kind).map(
                (b, i) => (
                  <div
                    className="form-box bar-lit"
                    data-tooltip={b.tooltip}
                    key={i}
                  >
                    <div
                      className="form-box-bg bento-fill"
                      style={{ background: b.color }}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const rates = data?.rates ?? {};
  const donuts: DonutConfig[] = teams.map((team) => {
    const shaped = shapeRateDonut(rates, team, ha, timeline);
    return {
      idNs: team.replace(/\s+/g, ""),
      dataValue: team,
      label: team,
      segments: shaped.segments,
      pct: shaped.pct,
      statsSpans: null,
    };
  });

  return (
    <HalfDonut
      key={fixture}
      dataAttr="team"
      idPrefix={cfg.idPrefix}
      hasStats={false}
      wrapperClass={cfg.donutView}
      donuts={donuts}
    />
  );
}
